import { services } from '@comall-backend-builder/core';
import moment from 'moment';
import { formatDateTime } from './ban-on-utils';

const api = services.api;

export const WHITELIST_PAGE_API = '/shelf_rule/whitelist/page';
export const WHITELIST_ADD_API = '/shelf_rule/whitelist/add';
export const WHITELIST_SPU_PAGE_API = '/shelf_rule/whitelist/product/spuPage';
export const WHITELIST_SKU_PAGE_API = '/shelf_rule/whitelist/product/skuPage';
export const WHITELIST_DELETE_API = '/shelf_rule/whitelist';

export const SCHEDULE_PAGE_API = '/shelf_rule/schedule/page';
export const SCHEDULE_CREATE_API = '/shelf_rule/schedule/create';
export const SCHEDULE_DETAIL_API = '/shelf_rule/schedule';
export const SCHEDULE_SPU_PAGE_API = '/shelf_rule/schedule/product/spuPage';
export const SCHEDULE_SKU_PAGE_API = '/shelf_rule/schedule/product/skuPage';
export const LOG_PAGE_API = '/shelf_rule/log/page';
export const PENDING_UNLIST_PAGE_API = '/shelf_rule/pending_unlist/page';

export const WHITELIST_TEMPLATE_TYPES = {
    LIST_BAN_SPU: 'SHELF_LIST_SPU',
    LIST_BAN_SKU: 'SHELF_LIST_SKU',
    UNLIST_BAN_SPU: 'SHELF_UNLIST_SPU',
    UNLIST_BAN_SKU: 'SHELF_UNLIST_SKU',
};

export const SCHEDULE_TEMPLATE_TYPES = {
    SPU: 'SHELF_SCHEDULE_SPU',
    SKU: 'SHELF_SCHEDULE_SKU',
};

export const getWhitelistTemplateType = (listType: string, dim: 'SPU' | 'SKU') => {
    if (listType === 'LIST_BAN') {
        return dim === 'SPU'
            ? WHITELIST_TEMPLATE_TYPES.LIST_BAN_SPU
            : WHITELIST_TEMPLATE_TYPES.LIST_BAN_SKU;
    }
    return dim === 'SPU'
        ? WHITELIST_TEMPLATE_TYPES.UNLIST_BAN_SPU
        : WHITELIST_TEMPLATE_TYPES.UNLIST_BAN_SKU;
};

export const getScheduleTemplateType = (dim: 'SPU' | 'SKU') => {
    return dim === 'SPU' ? SCHEDULE_TEMPLATE_TYPES.SPU : SCHEDULE_TEMPLATE_TYPES.SKU;
};

export const downloadWhitelistTemplate = (type: string) => {
    return api.download({}, { apiPath: `/templates/${type}` });
};

export const downloadScheduleTemplate = (type: string) => {
    return api.download({}, { apiPath: `/templates/${type}` });
};

export type AddWhitelistParams = {
    list_type: string;
    dimension: 'SPU' | 'SKU';
    object_codes: string[];
    remark: string;
};

export const fetchWhitelistPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: WHITELIST_PAGE_API });
};

export const addWhitelist = (params: AddWhitelistParams) => {
    return api
        .request({ method: 'post', apiPath: WHITELIST_ADD_API, params })
        .then(parseTextResponse);
};

export const fetchWhitelistSpuPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: WHITELIST_SPU_PAGE_API });
};

export const fetchWhitelistSkuPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: WHITELIST_SKU_PAGE_API });
};

export const fetchSchedulePage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: SCHEDULE_PAGE_API });
};

export type CreateScheduleParams = {
    name: string;
    dimension: 'SPU' | 'SKU';
    object_codes: string[];
    shelf_time?: string;
    unshelf_time?: string;
    remark?: string;
};

export const createSchedule = (params: CreateScheduleParams) => {
    return api
        .request({ method: 'post', apiPath: SCHEDULE_CREATE_API, params })
        .then(parseTextResponse);
};

export const fetchScheduleSpuPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: SCHEDULE_SPU_PAGE_API });
};

export const fetchScheduleSkuPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: SCHEDULE_SKU_PAGE_API });
};

export const fetchScheduleDetail = (id: string | number) => {
    return api.get({}, { apiPath: `${SCHEDULE_DETAIL_API}/${id}` });
};

export const cancelSchedule = (id: string | number) => {
    return api.post({}, { apiPath: `${SCHEDULE_DETAIL_API}/${id}/cancel` });
};

export const fetchLogPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: LOG_PAGE_API });
};

export const fetchPendingUnlistPage = (params: Record<string, any>) => {
    return api.get(params, { apiPath: PENDING_UNLIST_PAGE_API });
};

const parseTextResponse = (response: any) => {
    const text = (response?.text || '').trim();
    if (!text) {
        return response?.body ?? null;
    }
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

export const deleteWhitelist = (id: string | number) => {
    return api
        .request({ method: 'delete', apiPath: `${WHITELIST_DELETE_API}/${id}`, params: {} })
        .then(parseTextResponse);
};

const pickStringMessage = (value: unknown): string => {
    if (typeof value === 'string') {
        const text = value.trim();
        return text;
    }
    return '';
};

const pickNestedMessage = (value: unknown): string => {
    if (!value || typeof value !== 'object') {
        return '';
    }
    const obj = value as Record<string, unknown>;
    return (
        pickStringMessage(obj.msg) ||
        pickStringMessage(obj.message) ||
        pickStringMessage(obj.resultMsg) ||
        pickStringMessage(obj.result_msg) ||
        pickStringMessage(obj.result)
    );
};

export const pickAddMessage = (res: any): string => {
    if (typeof res === 'string') {
        return pickStringMessage(res);
    }
    if (res == null) {
        return '';
    }
    return (
        pickStringMessage(res.result) ||
        pickStringMessage(res.resultBody) ||
        pickStringMessage(res.data) ||
        pickStringMessage(res.msg) ||
        pickStringMessage(res.message) ||
        pickStringMessage(res.resultMsg) ||
        pickStringMessage(res.result_msg) ||
        pickNestedMessage(res.result) ||
        pickNestedMessage(res.data)
    );
};

export const assertBizSuccess = (res: any) => {
    if (res == null || typeof res.code === 'undefined') {
        return true;
    }
    if (Number(res.code) === 200) {
        return true;
    }
    return false;
};

export const pickPageResult = (res: any): { list: any[]; total: number } => {
    const raw = res?.result;
    const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.result)
        ? raw.result
        : Array.isArray(res?.resultList)
        ? res.resultList
        : [];
    const total = res?.totalNum || res?.total_num || raw?.totalNum || res?.total || 0;
    return { list, total };
};

export const mapWhitelistItem = (item: any) => ({
    id: item.id,
    code: item.object_code || item.objectCode || item.code,
    name: item.object_name || item.objectName || item.name,
    remark: item.remark,
    source: item.source,
    creator: item.created_by || item.createdBy || item.creator,
    createTime: item.create_time || item.createTime,
    taskId: item.task_id || item.taskId,
    listType: item.list_type || item.listType,
    dimension: item.dimension,
});

export const SCHEDULE_STATUS_LABEL: Record<string, string> = {
    WAIT_START: '待开始',
    RUNNING: '进行中',
    FINISHED: '已完成',
    CANCELLED: '已取消',
    PARTIAL_FAIL: '部分失败',
};

export const SCHEDULE_RESULT_LABEL: Record<string, string> = {
    WAIT: '待执行',
    SUCCESS: '成功',
    FAIL: '失败',
    SKIP: '跳过',
};

export const mapScheduleResult = (value?: string) => {
    if (!value) {
        return '-';
    }
    return SCHEDULE_RESULT_LABEL[value] || value;
};

export const mapScheduleDetailItem = (item: any) => {
    const shelfFail = item.shelf_fail_reason || item.shelfFailReason || '';
    const unshelfFail = item.unshelf_fail_reason || item.unshelfFailReason || '';
    const reasons = [shelfFail, unshelfFail].filter(Boolean);
    return {
        id: item.id || item.object_code || item.objectCode,
        objectCode: item.object_code || item.objectCode || '',
        objectName: item.object_name || item.objectName || '',
        removed: item.removed,
        shelfResult: mapScheduleResult(item.shelf_result || item.shelfResult),
        unshelfResult: mapScheduleResult(item.unshelf_result || item.unshelfResult),
        reason: reasons.length ? reasons.join('；') : '',
    };
};

export const pickScheduleDetail = (res: any) => {
    const raw = res?.result ?? res?.data ?? res;
    const taskRaw = raw?.task || raw;
    const itemsRaw = raw?.items || raw?.item_list || raw?.itemList || [];
    const task = mapScheduleItem(taskRaw || {});
    const items = Array.isArray(itemsRaw) ? itemsRaw.map(mapScheduleDetailItem) : [];
    return { task, items };
};

export const mapScheduleItem = (item: any) => {
    const statusCode = item.status || '';
    const shelfTime = item.shelf_time ?? item.shelfTime;
    const unshelfTime = item.unshelf_time ?? item.unshelfTime;
    const createTime = item.create_time ?? item.createTime;
    return {
        id: item.id,
        name: item.name,
        dimension: item.dimension,
        summary:
            item.summary ||
            item.object_summary ||
            item.objectSummary ||
            item.code_summary ||
            item.codeSummary ||
            '-',
        onTime: shelfTime != null && shelfTime !== '' ? formatDateTime(shelfTime) : '—',
        offTime: unshelfTime != null && unshelfTime !== '' ? formatDateTime(unshelfTime) : '—',
        remark: item.remark,
        status: SCHEDULE_STATUS_LABEL[statusCode] || statusCode || '-',
        statusCode,
        shelfExecuted: item.shelf_executed ?? item.shelfExecuted,
        unshelfExecuted: item.unshelf_executed ?? item.unshelfExecuted,
        creator: item.created_by || item.createdBy || item.creator,
        createTime: createTime != null && createTime !== '' ? formatDateTime(createTime) : '-',
    };
};

export const LOG_ACTION_LABEL: Record<string, string> = {
    AUTO_LIST: '自动上架',
    AUTO_UNLIST: '自动下架',
    CD_START: '开始倒计时',
    CD_CANCEL: '取消倒计时',
};

export const LOG_RESULT_LABEL: Record<string, string> = {
    SUCCESS: '成功',
    FAIL: '失败',
};

export const mapLogItem = (item: any, index = 0) => {
    const resultCode = item.result || '';
    const executeTime = item.execute_time ?? item.executeTime;
    const failReason = item.fail_reason ?? item.failReason;
    return {
        id: item.id || `${item.sku_code || item.skuCode || ''}_${executeTime || ''}_${index}`,
        sku: item.sku_code || item.skuCode || '',
        spu: item.spu_code || item.spuCode || '',
        action: LOG_ACTION_LABEL[item.action] || item.action || '-',
        reason: item.trigger_reason || item.triggerReason || '-',
        before: item.before_snapshot || item.beforeSnapshot || '-',
        after: item.after_snapshot || item.afterSnapshot || '-',
        executeTime: executeTime != null && executeTime !== '' ? formatDateTime(executeTime) : '-',
        result: LOG_RESULT_LABEL[resultCode] || resultCode || '-',
        resultCode,
        failReason: resultCode === 'FAIL' && failReason ? failReason : '-',
    };
};

export const PENDING_STATUS_LABEL: Record<string, string> = {
    RUNNING: '进行中',
    CANCELLED: '已取消',
    EXECUTED: '已执行',
};

export const calcPendingRemain = (statusCode?: string, expireTime?: string | number | null) => {
    if (statusCode === 'EXECUTED' || statusCode === '已执行') {
        return '已到期';
    }
    if (statusCode !== 'RUNNING' && statusCode !== '进行中') {
        return '-';
    }
    if (expireTime === undefined || expireTime === null || expireTime === '') {
        return '-';
    }
    const expire = moment(expireTime);
    if (!expire.isValid()) {
        return '-';
    }
    const diffMs = Math.max(expire.diff(moment()), 0);
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}小时${minutes}分`;
};

export const mapPendingUnlistItem = (item: any, index = 0) => {
    const statusCode = item.status || '';
    const startTime = item.start_time ?? item.startTime;
    const expireTime = item.expire_time ?? item.expireTime;
    return {
        id: item.id || `${item.sku_code || item.skuCode || ''}_${expireTime || ''}_${index}`,
        sku: item.sku_code || item.skuCode || '',
        spu: item.spu_code || item.spuCode || '',
        name: item.goods_name || item.goodsName || item.name || '',
        startTime: startTime != null && startTime !== '' ? formatDateTime(startTime) : '-',
        expireTime: expireTime != null && expireTime !== '' ? formatDateTime(expireTime) : '-',
        expireTimeRaw: expireTime,
        remain: calcPendingRemain(statusCode, expireTime),
        status: PENDING_STATUS_LABEL[statusCode] || statusCode || '-',
        statusCode,
        cancelReason: item.cancel_reason || item.cancelReason || '-',
    };
};
